import * as _ from "lodash";

import {
  _makeJHHApiCall,
  _makeInSecureApiCall,
  IMakeJHHApiCall,
  IMakeInSecureApiCall,
  _getAccessToken,
  _setItemInLocalEncrypted,
  _getItemFromLocalDecrypted,
} from "network-helper";
import {
  JHHMainEnv,
  JHHPatientAppEnv,
  JHHUploadEnv,
  JHHCorePlatform,
  JHHAppIntegrationService,
  relativePath,
  JHHActualEnv,
  fallbackErrorMessage,
  apimSubscriptionKey,
} from "../../Constants/config";
import { EStatus } from "../Enums/generic";
import { _setRequestToken, _getRefreshToken } from "./JHHToken";
import { inputParams } from "./GetCommonParam";
import { forceLogout } from "./ForceLogout";
import { notify } from "../../Components/Shared/Error/error";
import { apiErrorIntentHanlder } from "./JsBridgeHelper";
import { _setItemInLocal } from "./LocalKeys";

interface IFailedApiCallType {
  previousRest: IMakeJHHApiCall;
  isJHHTokenRequired: boolean;
  isDeviceDetailsRequired: boolean;
}

const callApiWithRefreshedToken = async (
  result: unknown,
  restDataObject: IFailedApiCallType
) => {
  const status = _.get(result, "status", "");
  if (status === EStatus.SUCCESS) {
    requestTokenCalled = false;
    requestTokenRetry++;
    const accessToken = _getAccessToken();
    _.set(restDataObject.previousRest, "headers.token", accessToken);
    return await handleApiCall(
      restDataObject.previousRest,
      restDataObject.isJHHTokenRequired,
      restDataObject.isDeviceDetailsRequired
    );
  }
};

let requestTokenCalled = false;
let requestTokenRetry = 0;
let awaitTokenPromise: Promise<unknown>;

export const _setAccessTokenHeader = async () => {
  const result = await _setRequestToken({});
  const status = _.get(result, "status", "");
  const token = _.get(result, "token", "");
  if (status === EStatus.SUCCESS) {
    return token;
  }
};

export const handleApiCall = async (
  { ...rest }: IMakeJHHApiCall,
  isJHHTokenRequired = false,
  isDeviceDetailsRequired = false,
  skipRequestToken = false,
  isSubscriptionKeyAvailable = false
): Promise<any> => {
  try {
    _.set(rest, "headers.Cache-Control", "public, max-age=86400");
    _.set(rest, "headers.Vary", "origin");
    if (isJHHTokenRequired === true) {
      const accessToken =
        _getAccessToken() === null
          ? await _setAccessTokenHeader()
          : _getAccessToken();
      _.set(rest, "headers.token", accessToken);
    }
    const EnvArray = [
      JHHMainEnv,
      JHHPatientAppEnv,
      JHHUploadEnv,
      JHHCorePlatform,
      JHHAppIntegrationService,
    ];
    if (
      rest.url.length &&
      (EnvArray.find((url) => rest.url.includes(url)) !== undefined || true)
    ) {
      if (isDeviceDetailsRequired) {
        rest = {
          ...rest,
          dataObj: {
            ...rest.dataObj,
            ...inputParams(),
          },
          headers: {
            ...rest.headers,
          },
        };

        if (!isSubscriptionKeyAvailable) {
          //@ts-ignore
          rest.headers["Ocp-Apim-Subscription-Key"] = apimSubscriptionKey;
        }
      } else {
        rest = {
          ...rest,
          headers: {
            ...rest.headers,
          },
        };
        if (!isSubscriptionKeyAvailable) {
          //@ts-ignore
          rest.headers["Ocp-Apim-Subscription-Key"] = apimSubscriptionKey;
        }
      }
    }
    if (requestTokenCalled === true && skipRequestToken === false) {
      const result = await awaitTokenPromise;
      const restDataObject = {
        previousRest: rest,
        isJHHTokenRequired: isJHHTokenRequired,
        isDeviceDetailsRequired: isDeviceDetailsRequired,
      };
      return await callApiWithRefreshedToken(result, restDataObject);
    }
    const clonedRest = _.cloneDeep(rest);
    const jhhAPIResponse = await _makeJHHApiCall({ ...clonedRest });
    if (
      rest.url.indexOf("refresh_token") === -1 &&
      rest.url.indexOf("request_token") === -1
    ) {
      requestTokenRetry = 0;
    }
    return jhhAPIResponse;
  } catch (err) {
    const statusCode = _.get(err, "response.status");
    const message = _.get(err, "message");
    const apiErrorMessage = _.get(
      err,
      "response.data.message",
      fallbackErrorMessage
    );
    if (statusCode === 401 && isJHHTokenRequired === true) {
      const restDataObject = {
        previousRest: rest,
        isJHHTokenRequired: isJHHTokenRequired,
        isDeviceDetailsRequired: isDeviceDetailsRequired,
      };
      if (requestTokenCalled === false && requestTokenRetry < 3) {
        awaitTokenPromise = _getRefreshToken();
        requestTokenCalled = true;
        const result = await awaitTokenPromise;
        return await callApiWithRefreshedToken(result, restDataObject);
      } else if (requestTokenCalled === true) {
        const result = await awaitTokenPromise;
        return await callApiWithRefreshedToken(result, restDataObject);
      } else if (requestTokenRetry >= 3) {
        let _hasBridgeHelper = await apiErrorIntentHanlder({
          value: "request limit exceeded",
          error_code: "401",
        });
        if (!_hasBridgeHelper) forceLogout();
      }
    } else if (statusCode === 412 && isJHHTokenRequired === true) {
      let _hasBridgeHelper = await apiErrorIntentHanlder({
        value: apiErrorMessage,
        error_code: "412",
      });
      if (!_hasBridgeHelper) forceLogout();
    } else if (statusCode === 410 && isJHHTokenRequired === true) {
      let _hasBridgeHelper = await apiErrorIntentHanlder({
        value: apiErrorMessage,
        error_code: "410",
      });
      if (!_hasBridgeHelper) {
        if (_getItemFromLocalDecrypted("switchToPrimaryProfile") !== "true") {
          _setItemInLocalEncrypted("switchToPrimaryProfile", "true");
          _setItemInLocal("_ne", apiErrorMessage);
          window.location.href = `${relativePath}/`;
        }
      }
    } else if (statusCode === 403 && isJHHTokenRequired === true) {
      let _hasBridgeHelper = await apiErrorIntentHanlder({
        value: apiErrorMessage,
        error_code: "403",
      });
      if (!_hasBridgeHelper) forceLogout();
    } else if (
      (statusCode === 500 || statusCode === 504) &&
      isJHHTokenRequired === true
    ) {
      notify(fallbackErrorMessage); //todo handle server error
    } else if (message === "Malformed UTF-8 data") {
      let _hasBridgeHelper = await apiErrorIntentHanlder({
        value: "Malformed UTF-8 data",
      });
      if (!_hasBridgeHelper) forceLogout();
    } else {
      throw err;
    }
  }
};

export const handleInSecureApiCall = async (
  { ...rest }: IMakeInSecureApiCall,
  isDeviceDetailsRequired = false
): Promise<any> => {
  try {
    const EnvArray = [
      JHHMainEnv,
      JHHPatientAppEnv,
      JHHUploadEnv,
      JHHCorePlatform,
      JHHActualEnv,
    ];
    if (
      rest.url.length &&
      EnvArray.find((url) => rest.url.includes(url)) !== undefined
    ) {
      if (isDeviceDetailsRequired) {
        rest = {
          ...rest,
          dataObj: {
            ...rest.dataObj,
            ...inputParams(),
          },
          headers: {
            ...rest.headers,
          },
        };
      } else {
        rest = {
          ...rest,
          headers: {
            ...rest.headers,
          },
        };
      }
    }
    const jhhAPIResponse = await _makeInSecureApiCall({ ...rest });
    return jhhAPIResponse;
  } catch (err) {
    throw err;
  }
};
